import { useEffect, useRef, useState } from 'react';

import { getSkillBuilderJob, SkillBuilderJob } from '../data/api';

const TERMINAL_STATUSES = new Set(['awaiting_approval', 'completed', 'failed', 'cancelled']);

const useSkillBuilderJob = (jobId: string | null, intervalMs = 10000, refreshKey = 0) => {
  const [job, setJob] = useState<SkillBuilderJob | null>(null);
  const [error, setError] = useState(false);
  const requestInFlight = useRef(false);
  const consecutiveFailures = useRef(0);

  useEffect(() => {
    if (!jobId) {
      setJob(null);
      setError(false);
      consecutiveFailures.current = 0;
      return undefined;
    }

    let cancelled = false;
    let intervalId: number | undefined;
    const poll = async () => {
      if (requestInFlight.current) { return; }
      requestInFlight.current = true;
      try {
        const nextJob = await getSkillBuilderJob(jobId);
        if (!cancelled) {
          setJob(nextJob);
          setError(false);
          consecutiveFailures.current = 0;
          if (TERMINAL_STATUSES.has(nextJob.status) && intervalId !== undefined) {
            window.clearInterval(intervalId);
          }
        }
      } catch {
        consecutiveFailures.current += 1;
        // A just-created durable job can briefly be unavailable while the proxy/backend
        // connection is settling. Avoid alarming authors for one-off polling failures.
        if (!cancelled) { setError(consecutiveFailures.current >= 3); }
      } finally {
        requestInFlight.current = false;
      }
    };

    poll();
    intervalId = window.setInterval(poll, intervalMs);
    return () => {
      cancelled = true;
      if (intervalId !== undefined) {
        window.clearInterval(intervalId);
      }
    };
  }, [intervalMs, jobId, refreshKey]);

  return { job, error, isTerminal: Boolean(job && TERMINAL_STATUSES.has(job.status)) };
};

export default useSkillBuilderJob;
